import React, { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ArrowDownUpIcon, CheckIcon, Grid2x2Icon, Rows3Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Spinner } from '@/components/ui/spinner';
import { Toggle } from '@/components/ui/toggle';
import { useIsMobile } from '@/hooks/use-mobile';
import { ListDisplayTypes, sortByOptions } from '../../constants';
import * as hooks from '../../hooks';
import { useMemoCompare } from '../../ui/hooks';
import { useSearchApiContext } from './contexts';
import { SearchBoxFilterButton } from './SearchBoxFilterButton';
import type { SortField } from './types';

type Sort = { field: string | null; by: string | null };
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/useSearchConfig.js
const useSortQueryParams = (): [Sort, (next?: Sort) => void] => (hooks as any).useSortQueryParams();

export type ListSearchControlsProps = {
  /** The header slot's height, as ListSearchDataRenderer passes it. */
  height?: number;
  /** The header slot's style, flattened by ListSearchDataRenderer. */
  style?: CSSProperties;
  heading?: string;
  searchPlaceholder?: string;
  headingRight?: ReactNode;
  showDisplayTypeToggle?: boolean;
};

/** A list's toolbar: its heading, the search box and filter panel, the cards/table toggle and Sort. */
export const ListSearchControls = React.memo(function ListSearchControls(props: ListSearchControlsProps) {
  const { height, style, heading = '', searchPlaceholder = '', headingRight = undefined, showDisplayTypeToggle = true } = props;
  const { loadingFromCache, displayType, toggleDisplayType, searchOptions } = useSearchApiContext();
  const isMobile = useIsMobile();
  const isTableDisplay = displayType === ListDisplayTypes.TABLE;

  return (
    <div className="flex w-full flex-col justify-end self-stretch bg-background" style={{ ...style, ...(height !== undefined ? { height } : null) }}>
      <div className={headingRight ? 'flex h-14 w-full items-center justify-between' : 'flex h-14 w-full items-center'}>
        <h3 className="pl-2 text-2xl font-medium">{heading}</h3>
        {isMobile && loadingFromCache ? <Spinner /> : null}
        {headingRight}
      </div>
      <div className="flex flex-col items-stretch justify-start gap-2 px-2 sm:flex-row sm:items-center sm:justify-between sm:px-2.5">
        <SearchBoxFilterButton searchPlaceholder={searchPlaceholder} />
        {!isMobile && loadingFromCache ? <Spinner /> : null}
        <div className="flex flex-row items-center justify-end gap-2">
          {!isMobile && showDisplayTypeToggle ? (
            <Toggle aria-label="Table view" pressed={isTableDisplay} onPressedChange={() => toggleDisplayType()} className="size-[38px]">
              {isTableDisplay ? <Grid2x2Icon className="size-[22px]!" /> : <Rows3Icon className="size-[22px]!" />}
            </Toggle>
          ) : null}
          <SortButton options={searchOptions.sort} />
        </div>
      </div>
    </div>
  );
});

/** Sort by: directions first, then fields. Choosing the current field again clears it. */
function SortButton({
  options: opts,
  label = 'Sort by',
  defaultSortBy = sortByOptions.ASC.name,
}: {
  options: Record<string, SortField>;
  label?: string;
  defaultSortBy?: string;
}) {
  const [{ field = null, by = null }, setQuery] = useSortQueryParams();
  const [currentSort, setCurrentSort] = useState<Sort>({ field, by });
  const shouldUpdate = useRef(false);
  const options = useMemoCompare(opts) as Record<string, SortField>;
  const isMobile = useIsMobile();

  useEffect(() => {
    if (shouldUpdate.current) {
      shouldUpdate.current = false;
      if (!currentSort.field || !options[currentSort.field]) {
        setQuery();
      } else {
        setQuery({ field: currentSort.field, by: currentSort.by ? currentSort.by : by ? by : defaultSortBy });
      }
    } else if (currentSort.field !== field || currentSort.by !== by) {
      setCurrentSort({ field, by });
    }
  }, [field, by, currentSort, defaultSortBy, setQuery, options]);

  const fieldKeys = useMemo(() => Object.keys(options), [options]);
  const byKeys = Object.keys(sortByOptions) as (keyof typeof sortByOptions)[];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button variant="outline" className="h-10 gap-2 rounded-lg border-muted-foreground" aria-label={isMobile ? label : undefined} />}
      >
        <ArrowDownUpIcon aria-hidden className="size-5!" />
        <span className="font-medium">{label}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {byKeys.map((key) => {
          const value = sortByOptions[key].name;
          const selected = currentSort.by === value || (!currentSort.by && defaultSortBy === value);
          return (
            <SortItem
              key={key}
              selected={selected}
              label={sortByOptions[key].label}
              onSelect={() => {
                shouldUpdate.current = true;
                setCurrentSort((c) => ({ field: c.field, by: value }));
              }}
            />
          );
        })}
        <DropdownMenuSeparator />
        {fieldKeys.map((key) => {
          const option = options[key] as SortField;
          const selected = currentSort.field === option.name;
          return (
            <SortItem
              key={key}
              selected={selected}
              label={option.label ?? option.name}
              onSelect={() => {
                shouldUpdate.current = true;
                setCurrentSort((c) => ({ field: selected ? null : option.name, by: c.by }));
              }}
            />
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SortItem({ selected, label, onSelect }: { selected: boolean; label: string; onSelect: () => void }) {
  return (
    <DropdownMenuItem aria-selected={selected} onClick={onSelect} className="justify-between">
      {label}
      {selected ? <CheckIcon aria-hidden /> : null}
    </DropdownMenuItem>
  );
}
